import type { ReactNode } from 'react';
import { Sparkle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type BrandHeroTone = 'brand' | 'coral' | 'amber';
export type BrandHeroMotion = 'float' | 'shake' | 'none';

export interface BrandHeroCircleProps {
  children: ReactNode;
  /** Circle diameter scale. */
  size?: 'md' | 'lg';
  tone?: BrandHeroTone;
  motion?: BrandHeroMotion;
  showSparkles?: boolean;
  className?: string;
}

const SIZE_CLASS = {
  md: 'h-28 w-28',
  lg: 'h-44 w-44',
} as const;

const RING_CLASS = {
  brand: 'bs-pulse-brand',
  coral: 'bs-pulse-coral',
  amber: 'bs-pulse-amber',
} as const;

const CIRCLE_CLASS = {
  brand: 'border-brand-200/60 bg-gradient-to-br from-brand-100 to-brand-50',
  coral: 'border-coral-500/25 bg-gradient-to-br from-coral-50 to-coral-100',
  amber: 'border-coral-500/20 bg-gradient-to-br from-coral-50 to-brand-50',
} as const;

const MOTION_CLASS = {
  float: 'cs-float',
  shake: 'bs-shake',
  none: '',
} as const;

/**
 * Pulse rings + hero circle used by loaders and brand status pages.
 */
export function BrandHeroCircle({
  children,
  size = 'md',
  tone = 'brand',
  motion = 'float',
  showSparkles = false,
  className,
}: BrandHeroCircleProps): JSX.Element {
  const sizeClass = SIZE_CLASS[size];

  return (
    <div className={cn('relative mb-6 inline-block', className)}>
      <div className={cn('absolute inset-0 rounded-full', RING_CLASS[tone])} />
      <div
        className={cn('absolute inset-0 rounded-full', RING_CLASS[tone], 'bs-pulse-delay')}
      />

      <div className={cn('relative mx-auto', sizeClass, MOTION_CLASS[motion])}>
        <div
          className={cn(
            'relative flex items-center justify-center overflow-hidden rounded-full border shadow-card',
            sizeClass,
            CIRCLE_CLASS[tone],
          )}
        >
          {children}
          {showSparkles ? (
            <>
              <Sparkle
                className="cs-spark absolute top-3 right-4 h-3 w-3 text-brand-500"
                aria-hidden
              />
              <Sparkle
                className="cs-spark cs-spark-s2 absolute bottom-4 left-3 h-2.5 w-2.5 text-coral-500"
                aria-hidden
              />
              <Sparkle
                className="cs-spark cs-spark-s3 absolute top-1/2 right-2 h-2 w-2 text-brand-400"
                aria-hidden
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
